import { Box, Button, Chip, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import type { PageResult } from '../../shared/data/types';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { toPageError } from '../partners/PartnerListPage';
import { AgentFormDialog } from './AgentFormDialog';
import { agentRepositoryToken, defaultAgentListQuery } from './repository';
import type { AgentFilters, AgentListQuery, AgentSummary } from './types';

export function AgentListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(agentRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState<AgentListQuery>(defaultAgentListQuery);
  const [refreshKey, setRefreshKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refreshKey, repository],
    (page) => page.items.length === 0,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('AGENT_MANAGE');
  const columns = useMemo<readonly GridColDef<AgentSummary>[]>(
    () => [
      {
        field: 'partnerId',
        headerName: t('agents.fields.partner'),
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => <Link to={`/partners/${row.partnerId}`}>{row.partnerId}</Link>,
      },
      {
        field: 'code',
        headerName: t('agents.fields.code'),
        minWidth: 130,
        renderCell: ({ row }) => <Link to={`/agents/${row.id}`}>{row.code}</Link>,
      },
      { field: 'name', headerName: t('agents.fields.name'), flex: 1, minWidth: 180 },
      {
        field: 'status',
        headerName: t('agents.fields.status'),
        minWidth: 120,
        renderCell: ({ row }) => <AgentStatusChip status={row.status} />,
      },
      {
        field: 'defaultCurrencyCode',
        headerName: t('agents.fields.currency'),
        minWidth: 110,
        sortable: false,
      },
      {
        field: 'userCount',
        headerName: t('agents.fields.userCount'),
        minWidth: 110,
        sortable: false,
      },
      {
        field: 'activeUserCount',
        headerName: t('agents.fields.activeUserCount'),
        minWidth: 130,
        sortable: false,
      },
      {
        field: 'updatedAt',
        headerName: t('agents.fields.updatedAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
    ],
    [language, t],
  );

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <>
            <DataSourceBadge source={dataSource} />
            {canManage ? (
              <Button onClick={() => setCreateOpen(true)} variant="contained">
                {t('agents.actions.create')}
              </Button>
            ) : null}
          </>
        }
        subtitle={t('agents.subtitle')}
        title={t('agents.title')}
      />
      <AgentFiltersForm
        onApply={(filters) => setQuery((current) => ({ ...current, page: 1, filters }))}
        value={query.filters}
      />
      <PageState
        emptyMessage={t('agents.empty')}
        error={toPageError(resource.error, t('agents.errors.load'))}
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <AgentGrid
            columns={columns}
            onQueryChange={setQuery}
            page={resource.data}
            query={query}
          />
        )}
      </PageState>
      <AgentFormDialog
        onClose={() => setCreateOpen(false)}
        onSaved={() => setRefreshKey((key) => key + 1)}
        open={createOpen}
      />
    </Box>
  );
}

function AgentGrid({
  columns,
  onQueryChange,
  page,
  query,
}: {
  readonly columns: readonly GridColDef<AgentSummary>[];
  readonly onQueryChange: (query: AgentListQuery) => void;
  readonly page: PageResult<AgentSummary>;
  readonly query: AgentListQuery;
}) {
  const gridQuery = { ...query, page: query.page - 1 };
  const { t } = useTranslation();
  return (
    <Box
      aria-label={t('agents.gridLabel')}
      role="region"
      sx={{ maxWidth: '100%', overflowX: 'auto' }}
      tabIndex={0}
    >
      <ServerDataGrid
        columns={columns}
        disableRowSelectionOnClick
        onQueryChange={(next) => onQueryChange({ ...next, page: next.page + 1 })}
        page={page}
        query={gridQuery}
        sx={{ minWidth: 1180 }}
      />
    </Box>
  );
}

function AgentFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: AgentFilters) => void;
  readonly value: AgentFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<AgentFilters>({ defaultValues: value });
  return (
    <ListFilterPanel
      applyLabel={t('agents.actions.applyFilters')}
      ariaLabel={t('filterPanel.label', { page: t('agents.title') })}
      onClear={() => {
        reset(defaultAgentListQuery.filters);
        onApply(defaultAgentListQuery.filters);
      }}
      onSubmit={handleSubmit((filters) =>
        onApply({
          partnerId: filters.partnerId.trim(),
          code: filters.code.trim(),
          name: filters.name.trim(),
          status: filters.status,
        }),
      )}
      primaryFields={
        <>
          <TextField
            label={t('agents.filters.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField label={t('agents.filters.code')} size="small" {...register('code')} />
          <TextField label={t('agents.filters.name')} size="small" {...register('name')} />
          <TextField
            defaultValue=""
            label={t('agents.filters.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
        </>
      }
    />
  );
}

export function AgentStatusChip({ status }: { readonly status: AgentSummary['status'] }) {
  return (
    <Chip
      color={status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'warning' : 'default'}
      label={status}
      size="small"
    />
  );
}
